import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { supabase } from "@/integrations/supabase/client";
import {
  parseSearchMatchField,
  type FreelancerSearchMatch,
  type FreelancerSearchReason,
  type SearchTerm,
} from "@/components/freelancers/v2/searchMatch";

interface UseFreelancerSearchMatchParams {
  searchTerm: string;
  searchOnlyName: boolean;
  searchExact: boolean;
  freelancerIds: string[];
}

const NO_REASONS: FreelancerSearchReason[] = [];

/**
 * Por que cada freelancer da página apareceu na busca livre, numa chamada só
 * (como a disponibilidade). Sem resposta, o card fica só com as marcações.
 */
export function useFreelancerSearchMatch({
  searchTerm,
  searchOnlyName,
  searchExact,
  freelancerIds,
}: UseFreelancerSearchMatchParams): (freelancerId: string) => FreelancerSearchMatch | undefined {
  const term = searchTerm.trim();
  const ids = [...freelancerIds].sort();

  const { data: reasonsById } = useQuery({
    queryKey: ["freelancers-search-reasons", term, searchExact, ids],
    enabled: term.length > 0 && !searchOnlyName && ids.length > 0,
    staleTime: 30_000,
    retry: false,
    queryFn: async (): Promise<Map<string, FreelancerSearchReason[]>> => {
      // RPC criada na migração 320: os tipos gerados do Supabase ainda não a conhecem.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any).rpc("search_freelancers_match_reasons", {
        p_search: term,
        p_ids: ids,
        p_exact: searchExact,
      });
      if (error) throw error;
      const rows = (data ?? []) as Array<{ freelancer_id: string; field: string; snippet: string }>;
      const grouped = new Map<string, FreelancerSearchReason[]>();
      for (const row of rows) {
        const field = parseSearchMatchField(row.field);
        if (!field) continue;
        grouped.set(row.freelancer_id, [
          ...(grouped.get(row.freelancer_id) ?? []),
          { field, snippet: row.snippet },
        ]);
      }
      return grouped;
    },
  });

  const { data: wordForms, isSuccess: formsLoaded, isError: formsFailed } = useQuery({
    queryKey: ["freelancers-search-term-forms", term],
    enabled: term.length > 0 && !searchOnlyName,
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: async (): Promise<string[][] | null> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any).rpc("search_term_forms", { p_search: term });
      if (error) throw error;
      return (data ?? null) as string[][] | null;
    },
  });

  // Sem formas (agência, ou a chamada falhou) a busca é por trecho, e o texto digitado é o que marca.
  const resolvedFieldTerm: SearchTerm = useMemo(() => {
    if (formsFailed || (formsLoaded && !wordForms)) return term;
    return wordForms ? { forms: wordForms, lastWordByStart: !searchExact } : "";
  }, [formsFailed, formsLoaded, wordForms, term, searchExact]);

  return useCallback(
    (freelancerId: string) => {
      if (!term) return undefined;
      return {
        nameTerm: term,
        fieldTerm: searchOnlyName ? "" : resolvedFieldTerm,
        reasons: (!searchOnlyName && reasonsById?.get(freelancerId)) || NO_REASONS,
      };
    },
    [term, searchOnlyName, reasonsById, resolvedFieldTerm],
  );
}
